import React, { FC, useEffect, useRef, useState } from 'react';
import { FormControl, FormHelperText, InputAdornment, InputLabel, MenuItem, Select, SelectProps, TextField, useMediaQuery, useTheme } from '@mui/material';
import { CatchRegistrationWeightWrapper } from './CatchRegistrationWeight.styled';
import PostCatchBody from '../../../../../Models/CatchRegistrationModels/PostCatchBody';
import CatchRegistration from '../../../../../Models/CatchRegistrationModels/CatchRegistration';
import ScaleIcon from '@mui/icons-material/Scale';
import PercentIcon from '@mui/icons-material/Percent';
import CommentIcon from '@mui/icons-material/Comment';
import NumbersIcon from '@mui/icons-material/Numbers';
import WeightNote from '../../../../../Models/WeightNoteModels/WeightNote';
import WeightNoteTypes from '../../../../../Constants/WeightNoteTypes';

interface CatchRegistrationWeightProps {
  handleFormChange: (updatedForm: PostCatchBody) => void;
  formData: PostCatchBody;
  setIcePercentageHasChanged: (hasChanged: boolean) => void;
  weightItem: CatchRegistration | undefined;
  disabled: boolean;
  selectedWeightNote: WeightNote;
  isExported: boolean;
  /** (e) Scallops => Hörpudiskur */
  stockIsScallops: boolean;
  showAnimalCount?: boolean;
  splitWeight?: number;
  forwardRef?: React.RefObject<HTMLInputElement>;
}

/**
 * Functional component for CatchRegistrationWeight.
 * Displays the weight section of the catch registration modal.
 * @param props
 * - takes in a weightItem object
 * - takes in a function to be called when the form changes
 * - takes in a the selected weight note.
 * @returns {JSX} - Responsible for returning the weight section of the catch registration modal.
 */

const CatchRegistrationWeight: FC<CatchRegistrationWeightProps> = (props) => {
  const formData = props.formData ?? {};
  const hasFormValue = <K extends keyof PostCatchBody>(key: K) =>
    Object.prototype.hasOwnProperty.call(formData, key);
  const getFormValue = <K extends keyof PostCatchBody>(key: K) =>
    (formData[key] as PostCatchBody[K]) ?? undefined;

  const [selectOpen, setSelectOpen] = useState(false);
  const [icePercentageInputValue, setIcePercentageInputValue] = useState<string>('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isProductNote = props.selectedWeightNote.weightNoteType.id === WeightNoteTypes.PRODUCT_ID;
  const weightKey: 'weight' | 'sampleWeight' = isProductNote ? 'sampleWeight' : 'weight';
  const MAXIMUM_ICE_PERCENTAGE = props.stockIsScallops ? 4 : 3;
  const MAXIMUM_WEIGHT = 9999999;
  const MAXIMUM_ANIMAL_COUNT = 999;
  const MIN_WEIGHT = 0;

  const uniqueIcePercentages = Array.from(
    new Set(
      (props.selectedWeightNote.weights ?? [])
        .filter(w => w.icePercentage !== null && w.icePercentage !== undefined)
        .map(w => w.icePercentage)
    )
  );
  const exportedIcePercentageOnWeightNote =
    uniqueIcePercentages.length === 1 ? uniqueIcePercentages[0] : null;

  const icePercentageSelectRef = useRef<HTMLDivElement & SelectProps>(null);

  const formWeightValue = hasFormValue(weightKey) ? getFormValue(weightKey) : undefined;
  const weightFromForm = typeof formWeightValue === 'number' ? formWeightValue : undefined;
  const weightFromWeightItem = isProductNote ? props.weightItem?.sampleWeight : props.weightItem?.weight;
  const resolvedWeight = hasFormValue(weightKey)
    ? weightFromForm
    : weightFromWeightItem ?? undefined;
  const weightInputValue: number | '' = resolvedWeight === undefined || resolvedWeight === null
    ? ''
    : resolvedWeight === 0
      ? ''
      : resolvedWeight;

  const hasAnimalCountInForm = hasFormValue('animalCount');
  const animalCountFromForm = hasAnimalCountInForm ? getFormValue('animalCount') : undefined;
  const currentAnimalCount = hasAnimalCountInForm
    ? (animalCountFromForm === null ? null : typeof animalCountFromForm === 'number' ? animalCountFromForm : undefined)
    : props.weightItem?.animalCount;
  const animalCountInputValue: number | '' = currentAnimalCount === undefined || currentAnimalCount === null
    ? ''
    : currentAnimalCount;

  const hasIceInForm = hasFormValue('icePercentage');
  const iceFromForm = hasIceInForm ? getFormValue('icePercentage') : undefined;
  const currentIcePercentage = hasIceInForm
    ? (iceFromForm === null ? null : typeof iceFromForm === 'number' ? iceFromForm : undefined)
    : props.weightItem?.icePercentage;

  const icePercentageSelectValue = currentIcePercentage ?? '';

  const hasComment = hasFormValue('userComment');
  const commentFromForm = hasComment ? getFormValue('userComment') : undefined;
  const commentValue = hasComment
    ? (typeof commentFromForm === 'string' ? commentFromForm : '')
    : (props.weightItem?.userComment ?? '');

  useEffect(() => {
    if (
      !props.weightItem &&
      props.isExported &&
      exportedIcePercentageOnWeightNote !== null &&
      !hasIceInForm
    ) {
      props.handleFormChange({ icePercentage: exportedIcePercentageOnWeightNote });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    exportedIcePercentageOnWeightNote,
    hasIceInForm,
    props.handleFormChange,
    props.isExported,
    props.weightItem
  ]);

  useEffect(() => {
    if (currentIcePercentage === undefined || currentIcePercentage === null) {
      setIcePercentageInputValue('');
      return;
    }
    setIcePercentageInputValue(currentIcePercentage.toString());
  }, [currentIcePercentage]);

  // Effect hook to adjust icePercentage based on isExported status and stockIsScallops status
  useEffect(() => {
    if (typeof currentIcePercentage !== 'number') {
      return;
    }

    // Only enforce max when NOT exported
    if (!props.isExported && currentIcePercentage > MAXIMUM_ICE_PERCENTAGE) {
      props.handleFormChange({ icePercentage: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    MAXIMUM_ICE_PERCENTAGE,
    currentIcePercentage,
    props.handleFormChange,
    props.isExported
  ]);

  const handleWeightChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newWeight = Number(event.target.value);
    // Check if the number is an integer
    if (!Number.isInteger(newWeight)) return;
    if (newWeight < MIN_WEIGHT || newWeight > MAXIMUM_WEIGHT) return;
    if (props.splitWeight && newWeight > props.splitWeight - 1) return;
    const updatedForm: PostCatchBody = {};
    if (isProductNote) {
      updatedForm.sampleWeight = newWeight;
    } else {
      updatedForm.weight = newWeight;
    }
    props.handleFormChange(updatedForm);
  };

  const handleAnimalCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      props.handleFormChange({ animalCount: null, shouldClearAnimalCount: true });
      return;
    }

    const newAnimalCount = Number(event.target.value);
    if (!Number.isInteger(newAnimalCount)) return;
    if (newAnimalCount < 1 || newAnimalCount > MAXIMUM_ANIMAL_COUNT) return;

    props.handleFormChange({ animalCount: newAnimalCount, shouldClearAnimalCount: false });
  };

  const handleIcePercentageChange = (icePercentage: string) => {
    props.setIcePercentageHasChanged(true);
    // If input is empty, reset the state
    if (icePercentage.trim() === "") {
      setIcePercentageInputValue('');
      props.handleFormChange({ icePercentage: null });
      return;
    }

    // Allow only digits, "," or "."
    if (!/^[0-9.,]*$/.test(icePercentage)) {
      return;
    }

    // Normalize comma to dot
    const normalized = icePercentage.replace(',', '.');

    // Reject if multiple dots
    const dotCount = (normalized.match(/\./g) || []).length;
    if (dotCount > 1) {
      return;
    }

    // Allow user to type trailing dot/comma without committing invalid number
    if (normalized.endsWith('.')) {
      setIcePercentageInputValue(icePercentage);
      return;
    }

    // Validate that it's a parseable number
    const inputValue = parseFloat(normalized);
    if (isNaN(inputValue)) {
      return;
    }

    // Enforce max 2 decimal places
    const [intPart, decimalPart] = normalized.split(".");
    let limited = intPart;
    if (decimalPart !== undefined) {
      limited += "." + decimalPart.slice(0, 2);
    }

    const limitedValue = parseFloat(limited);

    if (!props.isExported && (limitedValue < 0 || limitedValue > MAXIMUM_ICE_PERCENTAGE)) {
      return;
    }

    setIcePercentageInputValue(limited);
    props.handleFormChange({
      icePercentage: limitedValue === 0 ? 0 : limitedValue,
    });
  };


  const handleFixedIcePercentageChange = (icePercentage: number) => {
    props.setIcePercentageHasChanged(true);
    const updatedForm: PostCatchBody = {
      icePercentage: icePercentage
    };
    props.handleFormChange(updatedForm);
    setSelectOpen(false); // Close the dropdown after selection.
  }

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const updatedForm: PostCatchBody = {
      userComment: value
    };
    props.handleFormChange(updatedForm);
  };

  return (
    <CatchRegistrationWeightWrapper>
      <div style={{ width: '100%', display: 'flex', flexDirection: isMobile || isTablet ? 'column' : 'row', gap: '1em' }}>
        <TextField
          inputRef={props.forwardRef}
          fullWidth
          type="number"
          disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
          id="weight"
          label={isProductNote ? "Vigtun úrtaks" : "Vigt"}
          variant="outlined"
          onChange={handleWeightChange}
          onKeyDown={(e) => {
            if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+" || e.key === ".") {
              e.preventDefault()
            }
          }}
          value={weightInputValue}
          InputProps={{
            inputProps: {
              min: MIN_WEIGHT,
              max: MAXIMUM_WEIGHT
            },
            startAdornment: (
              <InputAdornment position="start">
                <ScaleIcon color='primary' />
              </InputAdornment>
            )
          }}
        />
        {props.selectedWeightNote.weightNoteType.id !== WeightNoteTypes.TO_REWEIGHING &&
          props.selectedWeightNote.weightNoteType.id !== WeightNoteTypes.TO_HOME_REWEIGHING &&
          props.selectedWeightNote.weightNoteType.id !== WeightNoteTypes.PRODUCT_SAMPLE_ID &&
          !props.isExported &&
          (
            <TextField
              fullWidth
              type="text"
              inputMode='decimal'
              disabled={props.selectedWeightNote?.weightNoteType?.id === WeightNoteTypes.PRODUCT_ID || props.selectedWeightNote?.isClosed || props.disabled}
              id="icePercentage"
              label="Ís (%)"
              variant="outlined"
              onChange={(e) => handleIcePercentageChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
                  e.preventDefault()
                }
              }}
              value={icePercentageInputValue}
              InputProps={{
                inputProps: {
                  min: 0,
                  max: MAXIMUM_ICE_PERCENTAGE
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <PercentIcon color='primary' />
                  </InputAdornment>
                )
              }}
            />
          )}
        {props.showAnimalCount && (
          <TextField
            fullWidth
            type="number"
            disabled={props.selectedWeightNote.isClosed || props.disabled}
            id="animalCount"
            label="Fjöldi dýra"
            variant="outlined"
            onChange={handleAnimalCountChange}
            onKeyDown={(e) => {
              if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+" || e.key === ".") {
                e.preventDefault()
              }
            }}
            value={animalCountInputValue}
            InputProps={{
              inputProps: {
                min: 1,
                max: MAXIMUM_ANIMAL_COUNT
              },
              startAdornment: (
                <InputAdornment position="start">
                  <NumbersIcon color='primary' />
                </InputAdornment>
              )
            }}
          />
        )}
        {/** If the destiny is exported theese are the only availeable values to choose from */}
        {props.isExported && (
          <FormControl fullWidth
            onKeyDown={(e => {
              if (e.key === "Enter") {
                e.stopPropagation();
              }
            })}>
            <InputLabel >Ís (%)</InputLabel>
            <Select
              value={icePercentageSelectValue}
              open={selectOpen}
              onOpen={() => setSelectOpen(true)}
              onClose={() => setSelectOpen(false)}
              label="Ís (%)"
              onChange={(event) => handleFixedIcePercentageChange(Number(event.target.value))}
              ref={icePercentageSelectRef}
            >
              <MenuItem value={0}>0 %</MenuItem>
              <MenuItem value={0.6}>0,6 %</MenuItem>
              <MenuItem value={7}>7 %</MenuItem>
              <MenuItem value={12}>12 %</MenuItem>
            </Select>
            <FormHelperText>Fastar ísprósentur fyrir útflutning</FormHelperText>
          </FormControl>
        )}
      </div>
      <TextField
        sx={{ width: '100%' }}
        type="string"
        disabled={props.selectedWeightNote.isClosed ? true : props.disabled ? true : false}
        id="comment"
        label="Athugasemd"
        variant="outlined"
        onChange={handleCommentChange}
        value={commentValue}
        InputProps={{
          inputProps: {
            maxLength: 100
          },
          startAdornment: (
            <InputAdornment position="start">
              <CommentIcon color='primary' />
            </InputAdornment>
          )
        }}
      />
    </CatchRegistrationWeightWrapper>
  );
};

export default CatchRegistrationWeight;
